import { cleanTaskPrReview, reviewBadgeText, reviewerLabel, reviewerWhereLabel, type ReviewBadgeInput, type TaskPrReview } from '@shared/factory-review';
import type { MachineReviewerStatus, MachineReviewers, RunStep } from '@shared/types/factory';
import type { StatusMeta } from '@/components/status-tone';

/** T-36: how the second-model review shows on cards, the run page and the Fleet page. */

/**
 * "Reviewed: 3 fixed, 1 not real" (success), "Needs settling: 1 disputed" (warning), or once settled
 * with findings sent back to fix, "Reviewed: 3 fixed, 1 sent back" (info: the builder is on them).
 */
export function reviewBadgeMeta(review: ReviewBadgeInput): StatusMeta {
  const tone = review.needs_person || review.disputed > 0 ? 'warning' : review.sent_back || review.settled_as === 'asked' ? 'info' : 'success';
  return { label: reviewBadgeText(review), tone };
}

/** The badge's hint: who reviewed, in how many rounds, on which run, and what's left to settle (SF-T-74). */
export function reviewBadgeHint(review: TaskPrReview): string {
  const rounds = review.rounds === 1 ? '1 round' : `${review.rounds} rounds`;
  const run = review.run_number ? `R-${review.run_number}` : 'the run';
  const base = `Second-model review by ${reviewerWhereLabel(review.reviewer, review.model, review.machine)}, ${rounds}${review.run_number ? ` (R-${review.run_number})` : ''}`;
  if (review.disputed > 0) {
    return `${base}. Needs settling: ${review.disputed === 1 ? '1 disputed finding' : `${review.disputed} disputed findings`}, settled in the task's Decisions (leave it, ask the builder to fix it, or take an option of the builder's own).`;
  }
  if (review.needs_person) return `${base}. Needs settling: the builder didn't say what it did about ${run}'s findings; a reply on the task goes back to it.`;
  if (review.settled_as === 'accepted') return `${base}. Settled: the builder didn't report on it, and the PR was accepted as it is.`;
  if (review.settled_as === 'asked') return `${base}. Settled: the builder was asked to report what it did about the findings.`;
  const settled = [review.left ? `${review.left} left as the builder says` : null, review.sent_back ? `${review.sent_back} sent back to the builder` : null].filter(Boolean);
  return settled.length ? `${base}. Settled: ${settled.join(', ')}.` : base;
}

/** A run's review, from its newest finished `review.verdict` step; null when it had none. */
export function runReview(steps: RunStep[], runNumber: number): TaskPrReview | null {
  const verdict = [...steps]
    .filter((s) => s.type === 'review.verdict' && s.status === 'succeeded' && s.result)
    .sort((a, b) => b.position - a.position)[0];
  if (!verdict?.result) return null;
  const r = verdict.result;
  return cleanTaskPrReview({
    reviewer: r.reviewer,
    model: r.model,
    fixed: r.fixed,
    not_real: r.not_real,
    disputed: r.disputed,
    rounds: r.round,
    run_number: runNumber,
    needs_person: r.needs_person === true,
    at: verdict.finished_at ?? '',
    machine: r.machine,
  });
}

/** One line under a review-stage step on the run page: what it found or decided; null for other steps. */
export function reviewStepSummary(step: RunStep): string | null {
  const r = step.result;
  if (!r || step.status !== 'succeeded') return null;
  const n = (v: unknown) => (typeof v === 'number' ? v : 0);
  switch (step.type) {
    case 'review.plan':
      return typeof r.reason === 'string' ? r.reason : null;
    case 'agent.checks.wait': {
      const fixes = n(r.fixes);
      const sha = typeof r.sha === 'string' ? ` on ${r.sha.slice(0, 7)}` : '';
      const tries = fixes ? ` after ${fixes} ${fixes === 1 ? 'fix' : 'fixes'}` : '';
      if (r.state === 'success') return `Checks passed${sha}${tries}.`;
      if (r.state === 'failure') return `Checks still fail${sha}${tries}: a person decides.`;
      if (r.state === 'none') return `No checks reported${sha}; carried on.`;
      if (r.state === 'pending') return `Checks were still going${sha}; carried on.`;
      return typeof r.reason === 'string' ? r.reason : "Couldn't read the checks; carried on.";
    }
    case 'agent.review': {
      const label = typeof r.label === 'string' ? r.label : reviewerLabel(String(r.reviewer ?? ''), typeof r.model === 'string' ? r.model : null);
      const minutes = typeof r.durationMs === 'number' ? ` in ${Math.max(1, Math.round(r.durationMs / 60_000))} min` : '';
      return `${label} reviewed${minutes}.${typeof r.stand_in === 'string' && r.stand_in ? ` ${r.stand_in}` : ''}`;
    }
    case 'review.verdict':
      // What the round came to, before anyone settled it (the task's badge shows the settled result).
      return reviewBadgeText({ fixed: n(r.fixed), not_real: n(r.not_real), disputed: n(r.disputed), needs_person: r.needs_person === true });
    default:
      return null;
  }
}

function reviewerText(name: string, s: MachineReviewerStatus): string {
  if (!s.installed) return `${name}: not installed`;
  if (s.logged_in === true) return `${name}: ready`;
  if (s.logged_in === false) return `${name}: not signed in`;
  return `${name}: installed`;
}

/** "Codex: ready · Cursor CLI: not installed", for the Fleet page; null before the agent reports them. */
export function machineReviewersText(reviewers: MachineReviewers | null | undefined): string | null {
  if (!reviewers) return null;
  // SF-T-104: whether its agent can stand in for another machine's review (older agents can't).
  const remote = reviewers.remote_review ? "reviews other machines' PRs" : "can't review other machines' PRs (update the agent)";
  return [reviewerText('Codex', reviewers.codex), reviewerText('Cursor CLI', reviewers.cursor), remote].join(' · ');
}
